import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, resolveImg } from "@/lib/api";
import { useFavorites } from "@/context/FavoritesContext";
import { formatPrice, formatKm } from "@/components/VehicleCard";
import { LeadForm } from "@/components/LeadForm";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  ArrowLeft, Calendar, Gauge, Fuel, Cog, Palette, DoorOpen, CheckCircle2, Loader2, Phone, Heart,
} from "lucide-react";

const specIcons = { Année: Calendar, Kilométrage: Gauge, Énergie: Fuel, Boîte: Cog, Couleur: Palette, Portes: DoorOpen };

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isFavorite, toggle } = useFavorites();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/vehicles/${id}`).then((r) => { setV(r.data); setActive(0); }).catch(() => setV(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-40"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>;
  if (!v) return (
    <div className="max-w-3xl mx-auto px-5 py-40 text-center">
      <h2 className="font-display text-3xl text-white">Véhicule introuvable</h2>
      <Link to="/vehicules"><Button className="mt-6 bg-[#C5A059] text-black rounded-sm">Retour aux véhicules</Button></Link>
    </div>
  );

  const specs = [
    ["Année", v.year], ["Kilométrage", formatKm(v.mileage)], ["Énergie", v.fuel],
    ["Boîte", v.transmission], ["Couleur", v.color || "—"], ["Portes", v.doors],
  ];
  const images = v.images?.length ? v.images.map(resolveImg) : ["https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200"];
  const fav = isFavorite(v.id);

  return (
    <div data-testid="vehicle-detail-page" className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-8">
        <ArrowLeft className="h-4 w-4" /> Retour
      </button>

      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <div className="relative aspect-[16/11] overflow-hidden rounded-sm border border-white/10 bg-black">
            <img src={images[active]} alt={v.model} className="h-full w-full object-cover" />
            {v.status !== "disponible" && <span className="absolute top-4 left-4 bg-[#D92D20] text-white text-xs font-semibold px-3 py-1 rounded-sm uppercase">Vendu</span>}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-3">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} className={`aspect-video w-24 overflow-hidden rounded-sm border ${i === active ? "border-[#C5A059]" : "border-white/10"}`}>
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059] mb-2">{v.brand}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-white leading-tight">{v.model}</h1>
          <p className="mt-4 font-display text-3xl font-bold text-[#C5A059]">{formatPrice(v.price)}</p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
            {specs.map(([label, value]) => {
              const Icon = specIcons[label] || Calendar;
              return (
                <div key={label} className="bg-[#121212] border border-white/10 rounded-sm p-4">
                  <Icon className="h-5 w-5 text-[#C5A059]" />
                  <p className="text-xs text-neutral-500 mt-3">{label}</p>
                  <p className="text-sm font-semibold text-white">{value}</p>
                </div>
              );
            })}
          </div>

          {v.description && <p className="mt-8 text-neutral-300 leading-relaxed">{v.description}</p>}

          {v.equipment?.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Équipements</h3>
              <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                {v.equipment.map((e) => (
                  <span key={e} className="flex items-center gap-2 text-sm text-neutral-300"><CheckCircle2 className="h-4 w-4 text-[#C5A059]" /> {e}</span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 flex flex-wrap gap-3">
            <Dialog>
              <DialogTrigger asChild>
                <Button data-testid="request-vehicle-btn" className="bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm px-8 h-12 font-semibold">Je suis intéressé</Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-2xl">Demande — {v.brand} {v.model}</DialogTitle></DialogHeader>
                <LeadForm
                  type="achat"
                  vehicleId={v.id}
                  submitLabel="Envoyer ma demande"
                  fields={[{ name: "vehicle", label: "Véhicule concerné", type: "text", placeholder: `${v.brand} ${v.model} (${v.year})` }]}
                />
              </DialogContent>
            </Dialog>
            <a href="tel:0767876026"><Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-sm h-12"><Phone className="h-4 w-4 mr-2" /> 07 67 87 60 26</Button></a>
            <Button
              data-testid="detail-fav-btn"
              onClick={() => toggle(v.id)}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 rounded-sm h-12"
            >
              <Heart className={`h-4 w-4 mr-2 ${fav ? "fill-[#D92D20] text-[#D92D20]" : ""}`} /> {fav ? "En favori" : "Ajouter aux favoris"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
